import React, {useCallback, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent, useEffectItem} from 'helpers/hooks/utils';
import {useTable} from 'components/organisms/Providers/TableProvider/TableProvider';
import StyledDealflowTaskBreakdownGraphCard
  from 'components/organisms/Cards/DealflowTaskBreakdownGraphCard/DealflowTaskBreakdownGraphCard.styles';
import constants from 'helpers/constants';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import TasksBarChart from 'components/organisms/Charts/TasksBarChart/TasksBarChart';
import Box from 'components/atoms/Layout/Box/Box';
import {useClientStatisticTaskBreakdown} from 'services/client/statistic/statistic.utils';
import {P} from 'components/atoms/Text/Typography/Typography';
import utils from 'helpers/utils';
import SimpleLoader from 'components/molecules/Loaders/SimpleLoader/SimpleLoader';

const DealflowDealflowTaskBreakdownGraphCard = React.forwardRef((props, ref) => {
  const innerProps = useComponentProps(props, 'DealflowTaskBreakdownGraphCard');

  const tableProvider = useTable();
  const graphState = tableProvider.graphState;

  const [breakdown, setBreakdown] = useState(null);

  const listState = useEffectItem(tableProvider.appliedListState());
  const year = graphState.graphSettings['tasks']?.filter?.year;
  const months = graphState.graphSettings['tasks']?.filter?.months ?? 1;

  const [tasksBreakdown, isLoading] = useClientStatisticTaskBreakdown(
    year, months, null,
    listState.search, listState.filter);

  useEffect(() => {
    if (tasksBreakdown) {
      setBreakdown(tasksBreakdown);
    }
  }, [tasksBreakdown]);

  const fields = useMemo(() => {
    const initialOption = constants.data.periodMonths.find((opt) => opt.value === months);

    return [{
      name: 'months',
      type: constants.formFieldTypes.autocomplete,
      validation: constants.formFieldValidationTypes.text,
      placeholder: 'Select a period',
      initial: initialOption,
      FormFieldProps: {
        autoFocus: false,
        hiddenLabel: true,
        clearable: false,
        size: 'small'
      },
      options: constants.data.periodMonths
    }];
  }, [months]);

  const setGraphSettingsEvent = useEffectEvent(graphState.setGraphSettings);
  const handleFilterChange = useCallback((field, value) => {
    setGraphSettingsEvent?.((current) => ({
      ...current,
      'tasks': {
        ...current?.['tasks'],
        filter: {
          ...current?.['tasks']?.filter,
          months: value?.value ?? value
        }
      }
    }));
  }, [setGraphSettingsEvent]);

  const handleVisibilityChange = (visibility) => {
    graphState.setGraphSettings((current) => ({
      ...current,
      'tasks': {
        ...current?.['tasks'],
        visibility
      }
    }));
  }

  return <StyledDealflowTaskBreakdownGraphCard ref={ref} {...innerProps}
                                               title="Tasks"
                                               context={(fields ? <InlineForm onChange={handleFilterChange}
                                                                              fields={fields} /> : null)}>
    {(utils.isDefined(breakdown) && breakdown?.length > 0) ?
      <Box className="DealflowTaskBreakdownGraphCard-content">
        <TasksBarChart breakdown={breakdown}
                       showLegend={false}
                       isLoading={isLoading}
                       visibility={graphState.graphSettings['tasks']?.visibility}
                       onVisibilityChange={handleVisibilityChange} />
      </Box> : null}

    {(!utils.isDefined(breakdown) || breakdown?.length === 0) ? (
      (!utils.isDefined(breakdown) || isLoading) ?
        <SimpleLoader className="GraphCard-loader" /> :
        <Box className="GraphCard-empty">
          <P>No task data found</P>
        </Box>
    ) : null}
  </StyledDealflowTaskBreakdownGraphCard>
});

DealflowDealflowTaskBreakdownGraphCard.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ])
};

export default DealflowDealflowTaskBreakdownGraphCard;
